import React from 'react';

interface UserProgress {
    totalCoursesEnrolled: number;
    coursesCompleted: number;
    averageCourseProgress: number;
    totalPracticeAttempts: number;
    averagePracticeScore: number;
    bestPracticeScore: number;
    totalStudyMinutes: number;
    currentStudyStreak: number;
    longestStudyStreak: number;
    questionsAnswered: number;
    questionsCorrect: number;
    overallAccuracy: number;
}

interface StudyStatisticsProps {
    userProgress: UserProgress;
}

export default function StudyStatistics({ userProgress }: StudyStatisticsProps) {
    const hours = Math.floor(userProgress.totalStudyMinutes / 60);
    const minutes = userProgress.totalStudyMinutes % 60;

    return (
        <div className="space-y-6">
            {/* Overall Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Course Statistics */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">Course Statistics</h3>
                    <div className="space-y-4">
                        <StatRow
                            label="Total Courses Enrolled"
                            value={userProgress.totalCoursesEnrolled}
                        />
                        <StatRow
                            label="Courses Completed"
                            value={userProgress.coursesCompleted}
                        />
                        <StatRow
                            label="Average Course Progress"
                            value={userProgress.averageCourseProgress.toFixed(1)}
                            unit="%"
                        />
                    </div>
                </div>

                {/* Practice Statistics */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">Practice Statistics</h3>
                    <div className="space-y-4">
                        <StatRow
                            label="Total Practice Attempts"
                            value={userProgress.totalPracticeAttempts}
                        />
                        <StatRow
                            label="Average Score"
                            value={userProgress.averagePracticeScore.toFixed(1)}
                            unit="%"
                        />
                        <StatRow
                            label="Best Score"
                            value={userProgress.bestPracticeScore.toFixed(1)}
                            unit="%"
                        />
                    </div>
                </div>

                {/* Time Statistics */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">Time Statistics</h3>
                    <div className="space-y-4">
                        <StatRow
                            label="Total Study Time"
                            value={`${hours}h ${minutes}m`}
                        />
                        <StatRow
                            label="Current Streak"
                            value={userProgress.currentStudyStreak}
                            unit="days"
                        />
                        <StatRow
                            label="Longest Streak"
                            value={userProgress.longestStudyStreak}
                            unit="days"
                        />
                    </div>
                </div>

                {/* Accuracy Statistics */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">Accuracy Statistics</h3>
                    <div className="space-y-4">
                        <StatRow
                            label="Questions Answered"
                            value={userProgress.questionsAnswered}
                        />
                        <StatRow
                            label="Questions Correct"
                            value={userProgress.questionsCorrect}
                        />
                        <StatRow
                            label="Overall Accuracy"
                            value={userProgress.overallAccuracy.toFixed(1)}
                            unit="%"
                        />
                    </div>
                </div>
            </div>

            {/* Visual Progress Bars */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-6">Performance Overview</h3>
                
                <div className="space-y-6">
                    {/* Course Completion */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Course Completion Rate</span>
                            <span className="text-sm font-bold text-indigo-600">
                                {userProgress.totalCoursesEnrolled > 0 
                                    ? ((userProgress.coursesCompleted / userProgress.totalCoursesEnrolled) * 100).toFixed(1)
                                    : 0}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-indigo-600 h-3 rounded-full transition-all"
                                style={{
                                    width: userProgress.totalCoursesEnrolled > 0 
                                        ? `${(userProgress.coursesCompleted / userProgress.totalCoursesEnrolled) * 100}%`
                                        : '0%'
                                }}
                            />
                        </div>
                    </div>

                    {/* Average Course Progress */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Average Progress Across Courses</span>
                            <span className="text-sm font-bold text-blue-600">
                                {userProgress.averageCourseProgress.toFixed(1)}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-blue-600 h-3 rounded-full transition-all"
                                style={{ width: `${Math.min(100, userProgress.averageCourseProgress)}%` }}
                            />
                        </div>
                    </div>

                    {/* Practice Score */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Average Practice Score</span>
                            <span className="text-sm font-bold text-green-600">
                                {userProgress.averagePracticeScore.toFixed(1)}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-green-600 h-3 rounded-full transition-all"
                                style={{ width: `${userProgress.averagePracticeScore}%` }}
                            />
                        </div>
                    </div>

                    {/* Accuracy */}
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Overall Accuracy</span>
                            <span className="text-sm font-bold text-purple-600">
                                {userProgress.overallAccuracy.toFixed(1)}%
                            </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                                className="bg-purple-600 h-3 rounded-full transition-all"
                                style={{ width: `${userProgress.overallAccuracy}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Achievements/Badges */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-6">Milestones</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {userProgress.coursesCompleted > 0 && (
                        <AchievementBadge
                            icon="🎓"
                            title="Course Master"
                            description={`${userProgress.coursesCompleted} courses completed`}
                        />
                    )}
                    {userProgress.totalPracticeAttempts >= 10 && (
                        <AchievementBadge
                            icon="🎯"
                            title="Practice Warrior"
                            description={`${userProgress.totalPracticeAttempts} tests taken`}
                        />
                    )}
                    {userProgress.currentStudyStreak >= 7 && (
                        <AchievementBadge
                            icon="🔥"
                            title="On Fire"
                            description={`${userProgress.currentStudyStreak} day streak`}
                        />
                    )}
                    {userProgress.overallAccuracy >= 85 && (
                        <AchievementBadge
                            icon="⭐"
                            title="High Achiever"
                            description={`${userProgress.overallAccuracy.toFixed(1)}% accuracy`}
                        />
                    )}
                    {userProgress.longestStudyStreak >= 30 && (
                        <AchievementBadge
                            icon="🏆"
                            title="Marathon Runner"
                            description={`${userProgress.longestStudyStreak} day record`}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

interface StatRowProps {
    label: string;
    value: string | number;
    unit?: string;
}

function StatRow({ label, value, unit }: StatRowProps) {
    return (
        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-600">{label}</span>
            <span className="text-lg font-bold text-gray-800">
                {value}{unit && <span className="text-sm ml-1 text-gray-500">{unit}</span>}
            </span>
        </div>
    );
}

interface AchievementBadgeProps {
    icon: string;
    title: string;
    description: string;
}

function AchievementBadge({ icon, title, description }: AchievementBadgeProps) {
    return (
        <div className="p-4 border-2 border-yellow-400 rounded-lg bg-yellow-50 text-center">
            <div className="text-3xl mb-2">{icon}</div>
            <p className="font-semibold text-gray-800 text-sm">{title}</p>
            <p className="text-xs text-gray-600 mt-1">{description}</p>
        </div>
    );
}
